import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID, REQUEST } from '@angular/core';
import { Request } from 'express';
import { Observable, catchError, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private tokenFetchFailed = false;
  private metadataUrl =
    'http://metadata.google.internal/computeMetadata/v1/instance/service-accounts/default/identity?audience=';
  private cachedToken: { token: string; expiry: number } | null = null;

  // Inject the REQUEST object for server-side rendering if using express-engine
  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    @Optional() @Inject(REQUEST) private request: Request | null // Make optional
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only intercept requests to our BFF API URL
    if (!environment.apiUrl || !req.url.startsWith(environment.apiUrl)) {
      return next.handle(req);
    }

    // --- Server-Side (Cloud Run) Token Fetch ---
    if (isPlatformServer(this.platformId)) {
      // Check if running on Cloud Run by checking for specific env var or header
      // The 'K_SERVICE' environment variable is usually present on Cloud Run
      // Or check for X-Cloud-Trace-Context header from the injected request
      const isCloudRun = process.env['K_SERVICE'] || this.request?.headers?.['x-cloud-trace-context'];

      if (!isCloudRun) {
        // Not on Cloud Run (e.g., local SSR build), don't try to fetch token
        console.warn('AuthInterceptor (SSR): Not running on Cloud Run, skipping token fetch.');
        return next.handle(req);
      }
      if (this.tokenFetchFailed) {
        console.error('AuthInterceptor (SSR): Token fetch previously failed, skipping.');
        return next.handle(req); // Or throw error
      }

      // Use cached token if valid
      if (this.cachedToken && this.cachedToken.expiry > Date.now() + 60000) {
        // Add 1 min buffer
        return this.addAuthHeader(req, next, this.cachedToken.token);
      }

      // Fetch token from metadata server
      return from(this.fetchTokenFromServer()).pipe(
        switchMap(token => this.addAuthHeader(req, next, token)),
        catchError(error => {
          console.error('AuthInterceptor (SSR): Error fetching/adding token:', error);
          this.tokenFetchFailed = true;
          return next.handle(req); // Proceed without token on failure
        })
      );
    }
    // --- Browser-Side (No Token Needed for this setup) ---
    // If running in the browser, we don't add the token for this specific setup
    // as the GET endpoints are protected by the OIDC guard expecting a server-side token.
    // If you had browser-authenticated endpoints later, you'd handle that here.
    else if (isPlatformBrowser(this.platformId)) {
      return next.handle(
        req.clone({
          headers: req.headers.set('Accept', 'application/json'),
        })
      );
    }
    // --- Fallback / Other Platforms ---
    else {
      return next.handle(req);
    }
  }

  private fetchTokenFromServer(): Promise<string> {
    console.log(`AuthInterceptor (SSR): Fetching token for audience: ${environment.apiUrl}`);
    return fetch(this.metadataUrl + environment.apiUrl, {
      // Audience MUST be the BFF URL
      headers: { 'Metadata-Flavor': 'Google' },
    }).then(async response => {
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch identity token (${response.status}): ${errorText}`);
      }
      const token = await response.text();
      // Decode token to find expiry (basic JWT decode, no verification needed here)
      try {
        const payload = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
        const expiry = payload.exp * 1000; // Convert seconds to milliseconds
        this.cachedToken = { token, expiry };
        console.log('AuthInterceptor (SSR): Token fetched successfully.');
      } catch (e) {
        console.error('AuthInterceptor (SSR): Failed to decode token payload', e);
        this.cachedToken = { token, expiry: Date.now() + 3540000 }; // Cache for ~59 mins if decode fails
      }
      return token;
    });
  }

  private addAuthHeader(req: HttpRequest<any>, next: HttpHandler, token: string): Observable<HttpEvent<any>> {
    const authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`).set('Accept', 'application/json'),
    });
    return next.handle(authReq);
  }
}
